import CallOutlinedIcon from "@mui/icons-material/CallOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import { Alert, Button, CircularProgress, Dialog, IconButton, ToggleButton, ToggleButtonGroup, Tooltip } from "@mui/material";
import { useEffect, useId, useState } from "react";
import { fetchOssOrderDetail, getOssDocumentDownloadUrl } from "../../api/ossApi";
import { actionStatus, displayLabel } from "../../constants/labels";
import { OssStatusChip } from "../OssStatusChip";
import { OssActionHistoryPanel } from "./OssActionHistoryPanel";
import "./OssActionProcessDialog.css";

const ActionField = ({ label, multiline, ...props }) => {
  const id = useId();
  return (
    <div className="oss-action-field">
      <label htmlFor={id}>{label}</label>
      {multiline ? <textarea id={id} rows={2} {...props} /> : <input id={id} {...props} />}
    </div>
  );
};

const InfoCell = ({ label, value }) => (
  <div className="oss-action-info">
    <dt>{label}</dt><dd>{value ?? "-"}</dd>
  </div>
);

const documentTypes = { PURCHASE_ORDER: "발주서", ORDER_FORM: "주문서", CHANGE_ORDER: "변경 근거", SPEC: "규격서", DRAWING: "도면", CONTRACT: "계약서", SPECIFICATION: "사양서", OTHER: "기타 문서" };

const formatFileSize = (value) => {
  const size = Number(value || 0);
  if (!size) return "-";
  if (size >= 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  if (size >= 1024) return `${Math.round(size / 1024)} KB`;
  return `${size} B`;
};

const getCurrentDateTimeLocal = () => {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

const buildActionNote = ({
  mode,
  actionAt,
  contactTarget,
  contactChannel,
  actionReason,
  checkedContent,
  actionPlan,
  nextPlan,
  memo,
  item,
}) => {
  const prefix =
    mode === "NOTE"
      ? "[기록 저장]"
      : mode === "FOLLOW_UP"
        ? "[다시 확인 예약]"
        : "[완료 처리]";
  const lines = [
    `${prefix} ${item?.exception_title || "조치 필요"} 확인`,
    actionAt ? `조치 일시: ${actionAt.replace("T", " ")}` : "",
    contactTarget ? `확인 대상: ${contactTarget}` : "",
    contactChannel ? `확인 경로: ${contactChannel}` : "",
    actionReason ? `확인 사유: ${actionReason}` : "",
    checkedContent ? `확인 내용: ${checkedContent}` : "",
    actionPlan ? `조치 내용: ${actionPlan}` : "",
    nextPlan ? `후속 계획: ${nextPlan}` : "",
    memo ? `비고/메모: ${memo}` : "",
  ].filter(Boolean);
  return lines.join("\n");
};

const getTargetLine = (detail, item) =>
  (detail?.lines || []).find((line) => String(line.order_line_id) === String(item?.order_line_id)) || null;

const modeOptions = [
  {
    value: "DONE",
    label: "완료 처리",
    helper: "확인 결과를 남기고 조치 필요 목록에서 제외합니다.",
  },
  {
    value: "FOLLOW_UP",
    label: "다시 확인 예약",
      helper: "확인 내용을 저장하고, 선택한 날짜에 다시 확인하도록 예약합니다.",
  },
  {
    value: "NOTE",
    label: "기록만 저장",
      helper: "기록만 추가하며, 처리 상태와 다음 확인일은 변경하지 않습니다.",
  },
];

const historyText = (value) => {
  if (!value) return "-";
  const labels = {
    ORDER: "주문 상태",
    SCHEDULE: "일정 상태",
    ACTION: "조치 기록",
    RECEIVED: "주문 접수",
    WAITING: "일정 대기",
    CONFIRMED: "계획 확정",
    CHANGE_REVIEW: "변경 검토",
    CHECK_REQUIRED: "확인 필요",
    PLAN_REVIEW: "계획 검토",
    IN_PROGRESS: "재확인 예정",
    DONE: "처리 완료",
  };
  return String(value)
    .split(/(ORDER|SCHEDULE|ACTION|RECEIVED|WAITING|CONFIRMED|CHANGE_REVIEW|CHECK_REQUIRED|PLAN_REVIEW|IN_PROGRESS|DONE)/g)
    .map((part) => labels[part] || part)
    .join("");
};

export const OssActionProcessDialog = ({
  open,
  item,
  groupCode,
  projectCode,
  loading,
  onClose,
  onSubmit,
}) => {
  const [memo, setMemo] = useState("");
  const [actionAt, setActionAt] = useState(getCurrentDateTimeLocal());
  const [contactTarget, setContactTarget] = useState("");
  const [contactChannel, setContactChannel] = useState("전화");
  const [actionReason, setActionReason] = useState("");
  const [checkedContent, setCheckedContent] = useState("");
  const [actionPlan, setActionPlan] = useState("");
  const [nextPlan, setNextPlan] = useState("");
  const [mode, setMode] = useState("DONE");
  const [nextCheckDate, setNextCheckDate] = useState("");
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");

  useEffect(() => {
    if (!open) return;
    setMemo("");
    setActionAt(getCurrentDateTimeLocal());
    setContactTarget("");
    setContactChannel("전화");
    setActionReason(item?.exception_message || item?.exception_title || "");
    setCheckedContent("");
    setActionPlan("");
    setNextPlan("");
    setMode("DONE");
    setNextCheckDate("");
  }, [open, item?.order_line_id]);

  useEffect(() => {
    let alive = true;
    const loadDetail = async () => {
      if (!open || !item?.order_id || !groupCode || !projectCode) return;
      setDetailLoading(true);
      setDetail(null);
      setDetailError("");
      try {
        const data = await fetchOssOrderDetail(groupCode, projectCode, item.order_id);
        if (alive) setDetail(data);
      } catch (error) {
        if (alive) {
          setDetail(null);
          setDetailError(error.response?.data?.message || "주문 상세 정보를 불러오지 못했습니다.");
        }
      } finally {
        if (alive) setDetailLoading(false);
      }
    };
    loadDetail();
    return () => {
      alive = false;
    };
  }, [groupCode, item?.order_id, open, projectCode]);

  if (!item) return null;

  const isFollowUp = mode === "FOLLOW_UP";
  const isNoteOnly = mode === "NOTE";
  const hasActionEntry = [
    contactTarget,
    checkedContent,
    actionPlan,
    nextPlan,
    memo,
  ].some((value) => String(value || "").trim());
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowValue = new Date(tomorrow.getTime() - tomorrow.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
  const submitDisabled = loading || (isFollowUp && (!nextCheckDate || nextCheckDate < tomorrowValue || !hasActionEntry)) || (isNoteOnly && !hasActionEntry);
  const targetLine = getTargetLine(detail, item);
  const documents = detail?.documents || [];
  const histories = detail?.histories || [];
  const actions = detail?.actions || [];
  const historyRows = histories.length ? histories : actions;

  const orderInfo = detail?.order || {};
  const targetSummary = targetLine || item;
  const composedNote = buildActionNote({
    mode,
    actionAt,
    contactTarget,
    contactChannel,
    actionReason,
    checkedContent,
    actionPlan,
    nextPlan,
    memo,
    item,
  });

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth={false}
      aria-labelledby="oss-action-dialog-title"
      PaperProps={{ className: "oss-action-dialog" }}>
      <header className="oss-action-header">
        <span className="oss-action-heading-icon"><CallOutlinedIcon /></span>
        <div className="oss-action-heading">
          <h2 id="oss-action-dialog-title">조치 처리</h2>
          <p>{item.order_no} · {item.customer_name || "-"}</p>
        </div>
        <Tooltip title="닫기"><IconButton aria-label="조치 처리 닫기" onClick={onClose} disabled={loading}><CloseRoundedIcon /></IconButton></Tooltip>
      </header>

      <div className="oss-action-body">
        <section className="oss-action-summary" aria-label="처리 대상 및 주문 정보">
          <div className="oss-action-target">
            <div>
              <h3>{item.item_name_snapshot || "주문 품목"}</h3>
              <p><strong>{item.exception_title || "확인 필요"}</strong> · {item.exception_message || "주문 일정을 확인해 주세요."}</p>
            </div>
            <div className="oss-action-chips">
              <OssStatusChip value={item.schedule_status} /><OssStatusChip type="risk" value={item.risk_level} />
            </div>
          </div>
          <dl className="oss-action-summary-grid">
            <InfoCell label="고객 발주번호" value={orderInfo.customer_po_no || "-"} />
            <InfoCell label="납품처 / 현장" value={item.site_name || orderInfo.site_name || "-"} />
            <InfoCell label="요청 납기" value={item.requested_due_date} />
            <InfoCell label="착수 필요일" value={targetSummary.required_start_date} />
            <InfoCell label="다음 확인일" value={item.next_check_date || targetSummary.check_required_date} />
            <InfoCell label="착수 기준 여유일" value={targetSummary.available_margin_days == null ? "-" : `${targetSummary.available_margin_days}일`} />
          </dl>
        </section>

        <div className="oss-action-workspace">
          <section className="oss-action-editor" aria-labelledby="oss-action-record-title">
            <div className="oss-action-section-heading"><CallOutlinedIcon /><h3 id="oss-action-record-title">조치 기록</h3></div>
            <div className="oss-action-editor-scroll">
              <ToggleButtonGroup exclusive value={mode} aria-label="처리 방식" className="oss-action-modes"
                onChange={(_, value) => { if (value) setMode(value); }} disabled={loading}>
                {modeOptions.map((option) => <ToggleButton key={option.value} value={option.value}>{option.label}</ToggleButton>)}
              </ToggleButtonGroup>
              <p className="oss-action-mode-help">{modeOptions.find((option) => option.value === mode)?.helper}</p>
              <fieldset className="oss-action-fields" disabled={loading}>
                {isFollowUp && <div className="oss-action-followup">
                  <ActionField label="다음 확인일" type="date" min={tomorrowValue} required value={nextCheckDate} onChange={(event) => setNextCheckDate(event.target.value)} />
                  <span>내일 이후 날짜를 선택해 주세요.</span>
                </div>}
                <ActionField label="조치 일시" type="datetime-local" value={actionAt} onChange={(event) => setActionAt(event.target.value)} />
                <ActionField label="확인 대상" value={contactTarget} onChange={(event) => setContactTarget(event.target.value)} placeholder="담당자 또는 부서" />
                <ActionField label="확인 경로" value={contactChannel} onChange={(event) => setContactChannel(event.target.value)} placeholder="전화, 메일, 메신저" />
                <ActionField label="확인 사유" value={actionReason} onChange={(event) => setActionReason(event.target.value)} />
                <ActionField label="확인 내용" multiline value={checkedContent} onChange={(event) => setCheckedContent(event.target.value)} placeholder="확인한 사실과 답변" />
                <ActionField label="조치 내용" multiline value={actionPlan} onChange={(event) => setActionPlan(event.target.value)} placeholder="처리한 내용" />
                <ActionField label="후속 계획" multiline value={nextPlan} onChange={(event) => setNextPlan(event.target.value)} placeholder="이후 진행할 사항" />
                <ActionField label="비고 / 메모" multiline value={memo} onChange={(event) => setMemo(event.target.value)} placeholder="추가 참고사항 (선택)" />
              </fieldset>
            </div>
          </section>

          <aside className="oss-action-reference" aria-label="이력 및 문서">
            {detailError ? <Alert severity="warning">{detailError}</Alert> : null}
            <OssActionHistoryPanel key={`${open}-${item.order_id}-${item.order_line_id}`}
              rows={historyRows} loading={detailLoading} error={detailError} lines={detail?.lines}
              formatTitle={historyText}
              formatStatus={(row) => historyText(displayLabel(actionStatus, row.action_status || row.description))} />
            <section className="oss-action-documents" aria-labelledby="oss-action-documents-title">
              <div className="oss-action-section-heading"><DescriptionOutlinedIcon /><h3 id="oss-action-documents-title">문서 / 발주서</h3><span>{documents.length}건</span></div>
              <div className="oss-action-list" tabIndex={0} aria-label="문서 목록">
                {detailLoading ? <div className="oss-action-empty"><CircularProgress size={22} aria-label="문서 불러오는 중" /></div> :
                  documents.length ? documents.map((document) => (
                    <article className="oss-action-document-entry" key={document.document_id}>
                      <DescriptionOutlinedIcon className="oss-action-file-icon" />
                      <div>
                        <p className="oss-action-file-name">{document.file_name}</p>
                        <p className="oss-action-file-meta">{documentTypes[document.document_type] || "문서"} · {formatFileSize(document.file_size)}</p>
                      </div>
                      <Tooltip title={`${document.file_name} 새 창에서 열기`}>
                        <IconButton aria-label={`${document.file_name} 열기`} onClick={() => window.open(getOssDocumentDownloadUrl(groupCode, projectCode, document.document_id), "_blank", "noopener,noreferrer")}><OpenInNewRoundedIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    </article>
                  )) : <p className="oss-action-empty">{detailError ? "문서를 불러오지 못했습니다." : "등록된 문서가 없습니다."}</p>}
              </div>
            </section>
          </aside>
        </div>
      </div>
      <footer className="oss-action-footer">
        <Button onClick={onClose} color="inherit" disabled={loading}>닫기</Button>
        <Button variant="contained" disabled={submitDisabled}
          onClick={() => onSubmit({
            actionStatus: isFollowUp ? "IN_PROGRESS" : isNoteOnly ? "NOTE" : "DONE",
            resultNote: composedNote,
            nextCheckDate: isFollowUp ? nextCheckDate : undefined,
            checkResult: isFollowUp ? "UNDECIDED" : "ON_SCHEDULE",
          })}>
          {loading ? "저장 중..." : isFollowUp ? "다시 확인 예약" : isNoteOnly ? "기록 저장" : "완료 처리"}
        </Button>
      </footer>
    </Dialog>
  );
};
